import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { IconListDetails } from '@tabler/icons';
import { useParams } from 'react-router-dom';
import api from '../../../../services/api';
import SgqNc from '../../../../../ui-component/sgq/SgqNc';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'warning' },
    confirmed: { label: 'Confirmada', color: 'info' },
    in_progress: { label: 'Em Produção', color: 'primary' },
    finished: { label: 'Concluída', color: 'success' },
    cancelled: { label: 'Cancelada', color: 'default' }
};

const ProductionOrderDetail = () => {
    const { id } = useParams();
    const [appId, setAppId] = useState(null);
    const [order, setOrder] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [actionLoading, setActionLoading] = useState(false);
    const [finishDialogOpen, setFinishDialogOpen] = useState(false);
    const [quantityProduced, setQuantityProduced] = useState('');
    const mountedRef = useRef(true);

    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                if (mountedRef.current) setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadOrder = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/production_orders/${id}/`);
            if (mountedRef.current) {
                setOrder(res.data);
                setQuantityProduced(String(res.data.quantity_planned));
            }
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: 'Erro ao carregar ordem de produção' });
        }
    };

    const runAction = async (action) => {
        setActionLoading(true);
        try {
            await api.post(`v1/apps/${appId}/production_orders/${id}/${action}/`);
            loadOrder();
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao executar ação' });
        } finally {
            if (mountedRef.current) setActionLoading(false);
        }
    };

    const handleFinish = async () => {
        if (!quantityProduced || Number(quantityProduced) <= 0) {
            setStatus({ type: 'error', message: 'Informe a quantidade produzida' });
            return;
        }
        setActionLoading(true);
        try {
            await api.post(`v1/apps/${appId}/production_orders/${id}/finish/`, { quantity_produced: quantityProduced });
            setFinishDialogOpen(false);
            loadOrder();
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao finalizar ordem' });
        } finally {
            if (mountedRef.current) setActionLoading(false);
        }
    };

    if (!order) return null;

    const statusInfo = STATUS_LABEL[order.status] || { label: order.status, color: 'default' };

    return (
        <Box>
            <PageHeader icon={IconListDetails} gradient="blue" title={order.bom_name} subtitle={order.produto_variacao_name} />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
                <Chip label={statusInfo.label} color={statusInfo.color} />
                <SgqNc appId={appId} entityType="production_order" entityId={order.id} defaultTitle={`Desvio na ordem de produção - ${order.bom_name}`} />
            </Stack>

            <GlassCard sx={{ mb: 2 }}>
                <CardContent>
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell>Ficha técnica</TableCell>
                                <TableCell>{order.bom_name}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Depósito</TableCell>
                                <TableCell>{order.warehouse_name}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Quantidade planejada</TableCell>
                                <TableCell>{order.quantity_planned}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Quantidade produzida</TableCell>
                                <TableCell>{order.quantity_produced ?? '—'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Data da ordem</TableCell>
                                <TableCell>{order.order_date}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Stack direction="row" spacing={2}>
                {order.status === 'draft' && (
                    <Button variant="contained" onClick={() => runAction('confirm')} disabled={actionLoading}>
                        Confirmar (reservar materiais)
                    </Button>
                )}
                {order.status === 'confirmed' && (
                    <Button variant="contained" onClick={() => runAction('start')} disabled={actionLoading}>
                        Iniciar Produção (consumir materiais)
                    </Button>
                )}
                {order.status === 'in_progress' && (
                    <Button variant="contained" onClick={() => setFinishDialogOpen(true)} disabled={actionLoading}>
                        Finalizar
                    </Button>
                )}
                {['draft', 'confirmed', 'in_progress'].includes(order.status) && (
                    <Button variant="outlined" color="error" onClick={() => runAction('cancel')} disabled={actionLoading}>
                        Cancelar
                    </Button>
                )}
            </Stack>

            <Dialog open={finishDialogOpen} onClose={() => setFinishDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Finalizar Produção</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Informe a quantidade realmente produzida (pode ser diferente da planejada).
                    </Typography>
                    <TextField
                        label="Quantidade produzida"
                        type="number"
                        fullWidth
                        value={quantityProduced}
                        onChange={(e) => setQuantityProduced(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFinishDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleFinish} disabled={actionLoading}>
                        {actionLoading ? 'Finalizando...' : 'Finalizar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProductionOrderDetail;
